import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, ArrowRight, Download, AlertCircle, Edit3, Check, X, Copy } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { ReviewStatus, ExportJob, ExportOptions } from '../types';
import { POLLING_INTERVALS, DISCLAIMER } from '../constants';

// 清理重复编号和内容的工具函数
const cleanDuplicatedNumbering = (text: string): string => {
  if (!text) return text;
  
  let cleaned = text;
  
  // 1. 处理 "第一条第一条" 这样的重复
  cleaned = cleaned.replace(/^(第[一二三四五六七八九十百]+条)\1/, '$1');
  cleaned = cleaned.replace(/^(第\d+条)\1/, '$1');
  
  // 2. 处理编号重复: "1.21.2" -> "1.2", "10.410.4" -> "10.4"
  const doubledMatch = cleaned.match(/^(\d+\.\d+)\1(.*)$/s);
  if (doubledMatch) {
    const num = doubledMatch[1];
    const rest = doubledMatch[2];
    cleaned = num + rest;
  }
  
  // 3. 处理内容重复: "1.2 内容...1.2 内容..." -> "1.2 内容..."
  const numberingMatch = cleaned.match(/^(\d+\.\d+)\s+/);
  if (numberingMatch) {
    const numbering = numberingMatch[1];
    const escapedNum = numbering.replace(/\./g, '\\.');
    const pattern = new RegExp(`(^|\\s)(${escapedNum})\\s+`, 'g');
    const matches = [...cleaned.matchAll(pattern)];
    
    if (matches.length >= 2) {
      const firstEnd = matches[0].index! + matches[0][0].length;
      const secondStart = matches[1].index!;
      const secondEnd = matches[1].index! + matches[1][0].length;
      
      const firstContent = cleaned.substring(firstEnd, secondStart).trim();
      const secondContent = cleaned.substring(secondEnd).trim();
      
      // 检查内容是否相似（前30个字符）
      const checkLen = Math.min(30, Math.min(firstContent.length, secondContent.length));
      if (checkLen > 5 && firstContent.substring(0, checkLen) === secondContent.substring(0, checkLen)) {
        cleaned = numbering + ' ' + firstContent;
      }
    }
  }
  
  // 4. 处理多行文本中的重复行
  const lines = cleaned.split('\n');
  if (lines.length > 1) {
    const uniqueLines: string[] = [];
    const seen = new Set<string>();
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed && !seen.has(trimmed)) {
        seen.add(trimmed);
        uniqueLines.push(line);
      }
    }
    cleaned = uniqueLines.join('\n');
  }
  
  return cleaned;
};

// 复制到剪贴板的工具函数
const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  }
};

const ExportPage: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const { 
    clauses, 
    reviewResults,
    loadProject,
    loadClauses,
    loadReviewResults,
    createExport,
    pollExportStatus,
    currentExport,
    saveUserEdit,
  } = useApp();
  
  const [processing, setProcessing] = useState(false);
  const [exportJob, setExportJob] = useState<ExportJob | null>(null);
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    comparison_mode: 'single',
    include_appendix: true,
    include_summary: true,
    user_edit_priority: true,
  });
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // Language toggle: 'zh' for Chinese, 'en' for English
  const [language, setLanguage] = useState<'zh' | 'en'>('zh');
  
  // User edits state - stores manual edits for each clause
  const [userEdits, setUserEdits] = useState<Map<string, string>>(new Map());
  const [editingClauseId, setEditingClauseId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  // Load data
  useEffect(() => {
    if (projectId && projectId !== 'new') {
      loadProject(projectId);
      loadClauses(projectId);
      loadReviewResults(projectId);
    }
  }, [projectId]);

  // Update from context
  useEffect(() => {
    if (currentExport) {
      setExportJob(currentExport);
    }
  }, [currentExport]);

  // Get the final text for a clause (priority: user edit > AI suggestion > original)
  const getFinalText = (clauseId: string, originalText: string) => {
    // Check user edits first
    if (userEdits.has(clauseId)) {
      return cleanDuplicatedNumbering(userEdits.get(clauseId)!);
    }
    
    const review = reviewResults.get(clauseId);
    
    // Then AI suggestion for non-compliant clauses
    if (review?.status === ReviewStatus.NON_COMPLIANT) {
      // Use Chinese or English based on language toggle
      const suggestion = language === 'zh' ? review.suggested_cn : review.suggested_en;
      if (suggestion) {
        return cleanDuplicatedNumbering(suggestion);
      }
    }
    return cleanDuplicatedNumbering(originalText);
  };

  // 复制状态
  const [copiedId, setCopiedId] = useState<string | null>(null);
  
  // 处理复制
  const handleCopy = async (clauseId: string, text: string) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopiedId(clauseId);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  // Start editing a clause
  const handleStartEdit = (clauseId: string, currentText: string) => {
    setEditingClauseId(clauseId);
    setEditText(currentText);
  };

  // Save edit
  const handleSaveEdit = async (clauseId: string) => {
    if (!projectId) return;
    
    // Update local state
    setUserEdits(prev => {
      const newEdits = new Map(prev);
      newEdits.set(clauseId, editText);
      return newEdits;
    });
    
    // Save to backend
    try {
      await saveUserEdit(projectId, clauseId, [editText], editText);
    } catch (e) {
      console.error('Failed to save user edit:', e);
    }
    
    setEditingClauseId(null);
    setEditText('');
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditingClauseId(null);
    setEditText('');
  };

  // Reset to original/AI suggestion
  const handleResetEdit = (clauseId: string) => {
    setUserEdits(prev => {
      const newEdits = new Map(prev);
      newEdits.delete(clauseId);
      return newEdits;
    });
  };

  const handleExport = async () => {
    if (!projectId) return;
    
    setProcessing(true);
    
    try {
      const job = await createExport(projectId, 'docx', exportOptions);
      setExportJob(job);
      
      // Start polling
      pollingRef.current = setInterval(async () => {
        const status = await pollExportStatus(projectId, job.id);
        setExportJob(status);
        
        if (status.status === 'completed' || status.status === 'failed') {
          setProcessing(false);
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
          }
        }
      }, POLLING_INTERVALS.EXPORT_STATUS);
      
    } catch (e) {
      setProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (!exportJob?.download_url || !projectId) return;
    
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';
      const downloadUrl = `${baseUrl}/projects/${projectId}/export/${exportJob.id}/download`;
      
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'contract_modified.docx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.error('Download failed:', e);
    }
  };

  const downloadReady = exportJob?.status === 'completed' && exportJob.download_url;

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Header */}
      <div className="h-16 bg-white border-b border-slate-200 px-8 flex items-center justify-between shrink-0 z-20 shadow-sm">
        <div>
          <h1 className="text-lg font-bold text-slate-900">合并与导出</h1>
          <p className="text-xs text-slate-500">左侧为原文，右侧为AI建议修改版本（可手动编辑）</p>
        </div>
        <div className="flex gap-3 items-center">
          {/* Language Toggle */}
          <div className="flex items-center bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setLanguage('zh')}
              className={`px-3 py-1.5 text-sm rounded-md transition-all ${
                language === 'zh' 
                  ? 'bg-white text-slate-900 shadow-sm font-medium' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              中文
            </button>
            <button
              onClick={() => setLanguage('en')}
              className={`px-3 py-1.5 text-sm rounded-md transition-all ${
                language === 'en' 
                  ? 'bg-white text-slate-900 shadow-sm font-medium' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              English
            </button>
          </div>
          
          <select
            value={exportOptions.comparison_mode}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setExportOptions(prev => ({ 
              ...prev, 
              comparison_mode: e.target.value as 'single' | 'dual_column' | 'track_changes'
            }))}
            className="text-sm border border-slate-300 rounded-md px-3 py-2"
            disabled={processing}
          >
            <option value="single">单栏（仅修改后）</option>
            <option value="dual_column">双栏对照</option>
          </select>
          
          {!downloadReady ? (
            <button 
              onClick={handleExport}
              disabled={processing}
              className="flex items-center gap-2 px-6 py-2.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 font-bold transition-all disabled:opacity-70"
            >
              {processing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {exportJob?.current_stage || '处理中...'}
                  {exportJob?.progress ? ` ${Math.round(exportJob.progress)}%` : ''}
                </>
              ) : (
                <>
                  <FileText size={18} />
                  生成最终合同
                </>
              )}
            </button>
          ) : (
            <button 
              onClick={handleDownload}
              className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-bold transition-all"
            >
              <Download size={18} />
              下载 DOCX
            </button>
          )}
        </div>
      </div>

      {/* Export Error */}
      {exportJob?.status === 'failed' && (
        <div className="bg-rose-50 border-b border-rose-200 px-8 py-3 flex items-center gap-2 text-rose-700">
          <AlertCircle size={16} />
          <span className="text-sm">导出失败: {exportJob.error_message || '未知错误'}</span>
        </div>
      )}

      {/* Disclaimer */}
      <div className="bg-amber-50 border-b border-amber-200 px-8 py-2">
        <p className="text-xs text-amber-800">{DISCLAIMER.zh}</p>
      </div>

      {/* Diff View */}
      <div className="flex-1 flex overflow-hidden bg-slate-100">
        <div className="flex-1 flex max-w-7xl mx-auto w-full h-full">
          {/* Original Column */}
          <div className="flex-1 overflow-y-auto border-r border-slate-300 bg-white">
            <div className="sticky top-0 bg-slate-50 p-4 border-b border-slate-200 z-10 font-bold text-slate-500 uppercase text-xs tracking-wider">
              原文
            </div>
            <div className="p-8 space-y-8">
              {clauses.map(c => {
                const cleanedContent = cleanDuplicatedNumbering(c.content);
                const cleanedTitle = c.title ? cleanDuplicatedNumbering(c.title) : null;
                const cleanedClauseNo = c.clause_no ? cleanDuplicatedNumbering(c.clause_no) : null;
                
                return (
                  <div key={c.id} className="opacity-70 group hover:opacity-100 transition-opacity">
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="font-mono font-bold text-sm text-slate-400">
                        {cleanedClauseNo || `#${c.sequence}`}
                      </span>
                      {cleanedTitle && (
                        <span className="font-bold text-slate-700 text-sm">{cleanedTitle}</span>
                      )}
                      <button
                        onClick={() => handleCopy(`orig-${c.id}`, cleanedContent)}
                        className="ml-auto p-1 text-slate-300 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors opacity-0 group-hover:opacity-100"
                        title="复制原文"
                      >
                        {copiedId === `orig-${c.id}` ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                      </button>
                    </div>
                    <p className="font-serif text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">{cleanedContent}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Arrow Divider */}
          <div className="w-12 bg-slate-100 flex items-center justify-center border-r border-slate-300">
            <div className="p-2 bg-slate-200 rounded-full text-slate-400">
              <ArrowRight size={20} />
            </div>
          </div>

          {/* Final Column - Editable */}
          <div className="flex-1 overflow-y-auto bg-white">
            <div className="sticky top-0 bg-emerald-50 p-4 border-b border-emerald-100 z-10 font-bold text-emerald-700 uppercase text-xs tracking-wider flex justify-between items-center">
              <span>AI建议版本（{language === 'zh' ? '中文' : 'English'}）</span>
              <span className="text-[10px] bg-white px-2 py-0.5 rounded border border-emerald-200">
                {downloadReady ? '已生成' : '点击条款编辑'}
              </span>
            </div>
            <div className="p-8 space-y-8">
              {clauses.map(c => {
                const finalText = getFinalText(c.id, c.content);
                const cleanedClauseNo = c.clause_no ? cleanDuplicatedNumbering(c.clause_no) : null;
                const cleanedTitle = c.title ? cleanDuplicatedNumbering(c.title) : null;
                const isModified = finalText !== cleanDuplicatedNumbering(c.content);
                const isUserEdited = userEdits.has(c.id);
                const review = reviewResults.get(c.id);
                const isEditing = editingClauseId === c.id;
                
                return (
                  <div 
                    key={c.id} 
                    className={`p-4 rounded-lg transition-colors ${
                      isEditing 
                        ? 'bg-blue-50 border-2 border-blue-300' 
                        : isUserEdited 
                          ? 'bg-purple-50/50 border border-purple-200' 
                          : isModified 
                            ? 'bg-emerald-50/50 border border-emerald-100' 
                            : 'hover:bg-slate-50 cursor-pointer border border-transparent'
                    }`}
                  >
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="font-mono font-bold text-sm text-slate-400">
                        {cleanedClauseNo || `#${c.sequence}`}
                      </span>
                      {cleanedTitle && (
                        <span className="font-bold text-slate-700 text-sm">{cleanedTitle}</span>
                      )}
                      <div className="ml-auto flex items-center gap-2">
                        {isUserEdited && !isEditing && (
                          <>
                            <span className="text-[10px] uppercase font-bold text-purple-600 bg-white px-1.5 py-0.5 rounded border border-purple-200">
                              手动编辑
                            </span>
                            <button
                              onClick={() => handleResetEdit(c.id)}
                              className="text-[10px] text-slate-500 hover:text-rose-600 underline"
                            >
                              重置
                            </button>
                          </>
                        )}
                        {isModified && !isUserEdited && !isEditing && (
                          <span className="text-[10px] uppercase font-bold text-emerald-600 bg-white px-1.5 py-0.5 rounded border border-emerald-200">
                            AI建议
                          </span>
                        )}
                        {review?.status === ReviewStatus.EXEMPTED && (
                          <span className="text-[10px] uppercase font-bold text-blue-600 bg-white px-1.5 py-0.5 rounded border border-blue-200">
                            已豁免
                          </span>
                        )}
                        {review?.status === ReviewStatus.IGNORED && (
                          <span className="text-[10px] uppercase font-bold text-slate-500 bg-white px-1.5 py-0.5 rounded border border-slate-200">
                            已忽略
                          </span>
                        )}
                        {!isEditing && (
                          <>
                            <button
                              onClick={() => handleCopy(`final-${c.id}`, finalText)}
                              className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                              title="复制"
                            >
                              {copiedId === `final-${c.id}` ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                            </button>
                            <button
                              onClick={() => handleStartEdit(c.id, finalText)}
                              className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="编辑此条款"
                            >
                              <Edit3 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {isEditing ? (
                      <div className="space-y-3">
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          className="w-full p-3 border border-blue-300 rounded-lg font-serif text-slate-900 text-base leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                          rows={Math.max(4, editText.split('\n').length + 1)}
                          autoFocus
                        />
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={handleCancelEdit}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                          >
                            <X size={14} />
                            取消
                          </button>
                          <button
                            onClick={() => handleSaveEdit(c.id)}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
                          >
                            <Check size={14} />
                            保存
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p 
                        className="font-serif text-slate-900 text-base leading-relaxed whitespace-pre-wrap cursor-pointer"
                        onClick={() => handleStartEdit(c.id, finalText)}
                      >
                        {finalText}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportPage;
